import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { logger } from '../../../utils/logger';

const OptimizationStrategiesPanel = ({ cohortData, conversionData, behaviorData }) => {
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState('all'); // all, high, medium, low
  const [realMetrics, setRealMetrics] = useState({
    totalUsers: 0,
    projectedConversions: 0,
    revenueEstimate: 0
  });

  // Fetch real user data for Combined Impact Potential
  useEffect(() => {
    const fetchRealMetrics = async () => {
      try {
        // Get total users
        const { data: allUsers } = await supabase?.from('user_profiles')?.select('id');
        const totalUsers = allUsers?.length || 0;

        // Get current premium users
        const { data: premiumSubs } = await supabase?.from('user_subscriptions')
          ?.select('user_id, subscription_plans(plan_type, price_monthly)')
          ?.eq('status', 'active')
          ?.neq('subscription_plans.plan_type', 'basic');
        const currentPremiumUsers = premiumSubs?.length || 0;

        // Calculate conversion rate (current premium / total users)
        const currentConversionRate = totalUsers > 0 ? (currentPremiumUsers / totalUsers) : 0;

        // Estimate potential conversions with optimization strategies
        // Assume strategies could improve conversion by 25% (conservative estimate)
        const optimizationLift = 0.25;
        const targetConversionRate = currentConversionRate + (currentConversionRate * optimizationLift);
        const projectedConversions = Math.round(totalUsers * targetConversionRate);

        // Calculate average premium plan price
        const avgPrice = premiumSubs?.reduce((sum, sub) => {
          return sum + (parseFloat(sub?.subscription_plans?.price_monthly) || 0);
        }, 0) / (premiumSubs?.length || 1);

        // Estimate annual revenue potential (monthly price * 12 months)
        const revenueEstimate = Math.round(projectedConversions * avgPrice * 12);

        setRealMetrics({
          totalUsers,
          projectedConversions,
          revenueEstimate
        });
      } catch (err) {
        logger?.error('Failed to fetch real metrics for optimization panel', err);
        // Fallback to zeros on error
        setRealMetrics({
          totalUsers: 0,
          projectedConversions: 0,
          revenueEstimate: 0
        });
      }
    };

    fetchRealMetrics();
  }, []);

  // Generate optimization strategies based on data
  const strategies = [
    {
      id: 'early-engagement',
      title: 'Early Engagement Boost',
      priority: 'high',
      impact: 'High',
      effort: 'Medium',
      expectedLift: '+23% conversion',
      icon: 'Zap',
      color: 'success',
      description: 'Target users within first 48 hours with personalized onboarding flow',
      targetCohorts: ['New Users', 'First Week'],
      actionItems: [
        'Send welcome email with quick-start guide within 1 hour',
        'Trigger in-app tutorial for core features on second session',
        'Offer limited-time discount (20% off) for early adopters',
        'Schedule follow-up notification on day 3 if no check-in completed'
      ],
      metrics: {
        targetUsers: 2450,
        estimatedConversions: 564,
        projectedRevenue: '£15,792'
      }
    },
    {
      id: 'feature-adoption',
      title: 'Feature Adoption Campaign',
      priority: 'high',
      impact: 'High',
      effort: 'Low',
      expectedLift: '+18% retention',
      icon: 'Compass',
      color: 'primary',
      description: 'Guide users to discover and adopt premium features through contextual prompts',
      targetCohorts: ['Casual Users', 'Moderate Engagement'],
      actionItems: [
        'Implement contextual tooltips for premium features',
        'Create "Feature of the Week" email series',
        'Add progress badges for feature exploration',
        'Enable free trial of premium features for 7 days'
      ],
      metrics: {
        targetUsers: 6400,
        estimatedConversions: 1152,
        projectedRevenue: '£32,256'
      }
    },
    {
      id: 'churn-prevention',
      title: 'Churn Prevention Protocol',
      priority: 'high',
      impact: 'Medium',
      effort: 'High',
      expectedLift: '-35% churn rate',
      icon: 'Shield',
      color: 'error',
      description: 'Proactive intervention for at-risk users showing declining engagement',
      targetCohorts: ['At-Risk Users', 'Dormant'],
      actionItems: [
        'Identify users with 7+ days of inactivity',
        'Send personalized re-engagement email with value reminder',
        'Offer exclusive content or feature unlock',
        'Schedule customer success call for premium users'
      ],
      metrics: {
        targetUsers: 2100,
        estimatedConversions: 735,
        projectedRevenue: '£20,580'
      }
    },
    {
      id: 'premium-timing',
      title: 'Optimal Conversion Timing',
      priority: 'medium',
      impact: 'High',
      effort: 'Low',
      expectedLift: '+31% conversion',
      icon: 'Clock',
      color: 'warning',
      description: 'Present premium upgrade offers at optimal moments in user journey',
      targetCohorts: ['Premium Curious', 'Feature Explorers'],
      actionItems: [
        'Trigger upgrade prompt after 3rd symptom tracking session',
        'Show premium benefits after user views analytics 2+ times',
        'Offer time-limited discount on days 7-14 of usage',
        'Display social proof (testimonials) before pricing page'
      ],
      metrics: {
        targetUsers: 3200,
        estimatedConversions: 992,
        projectedRevenue: '£27,776'
      }
    },
    {
      id: 'social-proof',
      title: 'Social Proof Amplification',
      priority: 'medium',
      impact: 'Medium',
      effort: 'Low',
      expectedLift: '+15% trust',
      icon: 'Users',
      color: 'primary',
      description: 'Leverage testimonials and success stories to build trust and credibility',
      targetCohorts: ['All Users'],
      actionItems: [
        'Add rotating testimonials on dashboard',
        'Create case study library with real user stories',
        'Display "X users upgraded this week" counter',
        'Implement referral program with incentives'
      ],
      metrics: {
        targetUsers: 12450,
        estimatedConversions: 1868,
        projectedRevenue: '£52,304'
      }
    },
    {
      id: 'pricing-optimization',
      title: 'Pricing Strategy Refinement',
      priority: 'low',
      impact: 'High',
      effort: 'High',
      expectedLift: '+12% LTV',
      icon: 'DollarSign',
      color: 'success',
      description: 'Test and optimize pricing tiers based on user behavior and willingness to pay',
      targetCohorts: ['Power Users', 'Regular Users'],
      actionItems: [
        'A/B test annual vs monthly pricing emphasis',
        'Introduce mid-tier plan for casual users',
        'Offer usage-based pricing for enterprise',
        'Test price anchoring with higher-tier option'
      ],
      metrics: {
        targetUsers: 6050,
        estimatedConversions: 726,
        projectedRevenue: '£20,328'
      }
    }
  ];

  const filteredStrategies = priorityFilter === 'all'
    ? strategies
    : strategies?.filter(s => s?.priority === priorityFilter);

  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'bg-error/20 text-error',
      'medium': 'bg-warning/20 text-warning',
      'low': 'bg-info/20 text-info'
    };
    return colors?.[priority] || colors?.['medium'];
  };

  const getImpactBadge = (impact) => {
    const badges = {
      'High': 'bg-success/20 text-success',
      'Medium': 'bg-warning/20 text-warning',
      'Low': 'bg-muted text-muted-foreground'
    };
    return badges?.[impact] || badges?.['Medium'];
  };

  return (
    <div className="space-y-4">
      {/* Header with Filter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="Target" size={18} className="text-primary" />
          <h3 className="font-semibold text-foreground">Optimization Strategies</h3>
        </div>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e?.target?.value)}
          className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All Priorities</option>
          <option value="high">High Priority</option>
          <option value="medium">Medium Priority</option>
          <option value="low">Low Priority</option>
        </select>
      </div>

      {/* Strategy Cards */}
      <div className="space-y-3">
        {filteredStrategies?.map((strategy) => {
          const isSelected = selectedStrategy?.id === strategy?.id;

          return (
            <div
              key={strategy?.id}
              onClick={() => setSelectedStrategy(isSelected ? null : strategy)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                isSelected
                  ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50 hover:bg-muted/30'
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`p-2 rounded-lg bg-${strategy?.color} text-white`}>
                    <Icon name={strategy?.icon} size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-foreground">{strategy?.title}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(strategy?.priority)}`}>
                        {strategy?.priority?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{strategy?.description}</p>
                  </div>
                </div>
                <Icon
                  name={isSelected ? 'ChevronUp' : 'ChevronDown'}
                  size={20}
                  className="text-muted-foreground"
                />
              </div>

              {/* Quick Metrics */}
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="p-2 bg-background rounded">
                  <p className="text-xs text-muted-foreground mb-1">Impact</p>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${getImpactBadge(strategy?.impact)}`}>
                    {strategy?.impact}
                  </span>
                </div>
                <div className="p-2 bg-background rounded">
                  <p className="text-xs text-muted-foreground mb-1">Effort</p>
                  <p className="text-sm font-bold text-foreground">{strategy?.effort}</p>
                </div>
                <div className="p-2 bg-background rounded">
                  <p className="text-xs text-muted-foreground mb-1">Expected Lift</p>
                  <p className="text-sm font-bold text-success">{strategy?.expectedLift}</p>
                </div>
              </div>

              {/* Expanded Details */}
              {isSelected && (
                <div className="mt-4 pt-4 border-t border-border space-y-4">
                  {/* Target Cohorts */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Target Cohorts:</p>
                    <div className="flex flex-wrap gap-2">
                      {strategy?.targetCohorts?.map((cohort, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium"
                        >
                          {cohort}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Action Items */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Action Items:</p>
                    <div className="space-y-2">
                      {strategy?.actionItems?.map((item, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <Icon name="CheckCircle" size={14} className="text-success mt-0.5" />
                          <p className="text-xs text-foreground">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Projected Metrics */}
                  <div className="p-3 bg-gradient-to-r from-success/10 to-primary/10 rounded-lg border border-success/20">
                    <p className="text-xs font-medium text-foreground mb-2">Projected Impact:</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Target Users</p>
                        <p className="text-sm font-bold text-foreground">{strategy?.metrics?.targetUsers?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Est. Conversions</p>
                        <p className="text-sm font-bold text-primary">{strategy?.metrics?.estimatedConversions?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Projected Revenue</p>
                        <p className="text-sm font-bold text-success">{strategy?.metrics?.projectedRevenue}</p>
                      </div>
                    </div>
                  </div>

                  {/* Implementation Button */}
                  <button className="w-full py-2 bg-primary text-white rounded-lg font-medium text-sm hover:bg-primary/90 transition-smooth">
                    Implement Strategy
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Total Impact Summary */}
      <div className="p-4 bg-gradient-to-br from-primary/10 to-success/10 rounded-lg border border-primary/20">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="TrendingUp" size={18} className="text-success" />
          <h3 className="font-semibold text-foreground">Combined Impact Potential</h3>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Total Target Users</p>
            <p className="text-2xl font-bold text-foreground">
              {realMetrics?.totalUsers?.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Projected Conversions</p>
            <p className="text-2xl font-bold text-primary">
              {realMetrics?.projectedConversions?.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Revenue Potential</p>
            <p className="text-2xl font-bold text-success">
              £{realMetrics?.revenueEstimate?.toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OptimizationStrategiesPanel;